import { useState, useEffect, type CSSProperties, type KeyboardEvent } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { useArcheAction } from '../../hooks/useArcheAction';
import { useArcheContext } from '../../hooks/useArcheContext';
import { cn } from '@/lib/utils';

type ReorderMode = 'arrows' | 'drag' | 'both';

interface SortableBulletItemProps {
  id: string;
  item: string;
  index: number;
  itemCount: number;
  showDragHandle: boolean;
  showArrows: boolean;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (text: string) => void;
  onConfirmEdit: () => void;
  onCancelEdit: () => void;
  onStartEdit: (index: number) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  disabled: boolean;
}

function SortableBulletItem({
  id,
  item,
  index,
  itemCount,
  showDragHandle,
  showArrows,
  isEditing,
  editText,
  onEditTextChange,
  onConfirmEdit,
  onCancelEdit,
  onStartEdit,
  onRemove,
  onMove,
  disabled,
}: SortableBulletItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm"
    >
      {showDragHandle && (
        <button
          type="button"
          className="p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="text-muted-foreground select-none">&#8226;</span>
      {isEditing ? (
        <input
          type="text"
          value={editText}
          onChange={(e) => onEditTextChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onConfirmEdit();
            }
            if (e.key === 'Escape') onCancelEdit();
          }}
          onBlur={onConfirmEdit}
          autoFocus
          className="flex-1 bg-transparent border-none outline-none text-foreground"
        />
      ) : (
        <span className="flex-1 text-foreground">{item}</span>
      )}
      {!disabled && !isEditing && (
        <div className="flex items-center gap-1 shrink-0">
          {showArrows && (
            <>
              <button
                type="button"
                onClick={() => onMove(index, -1)}
                disabled={index === 0}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                title="Move up"
              >
                &#9650;
              </button>
              <button
                type="button"
                onClick={() => onMove(index, 1)}
                disabled={index === itemCount - 1}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                title="Move down"
              >
                &#9660;
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => onStartEdit(index)}
            className="p-1 text-muted-foreground hover:text-foreground"
            title="Edit"
          >
            &#9998;
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 text-muted-foreground hover:text-destructive"
            title="Delete"
          >
            &#10005;
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * BulletListInput component - structured input for building a list of bullet points.
 *
 * Supports add-on-enter, paste-split on newlines, inline editing,
 * delete, drag-and-drop reordering, and move up/down arrow reordering.
 */
export function BulletListInput({ id, props, style }: SDUIComponentProps) {
  const { updateInput } = useArcheAction();
  const { context } = useArcheContext();

  const {
    name = 'bullets',
    label,
    placeholder = 'Type a bullet point and press Enter',
    maxItems,
    disabled = false,
    reorderMode = 'both',
    className,
  } = props as {
    name?: string;
    label?: string;
    placeholder?: string;
    maxItems?: number;
    disabled?: boolean;
    reorderMode?: ReorderMode;
    className?: string;
  };

  const showDragHandle = !disabled && (reorderMode === 'drag' || reorderMode === 'both');
  const showArrows = !disabled && (reorderMode === 'arrows' || reorderMode === 'both');

  // Initialize the input state key to [] if it doesn't exist yet.
  // This ensures binding expressions like {{input.bullets.length === 0}}
  // work from the first render (otherwise input.bullets is undefined and
  // undefined.length evaluates to undefined, not 0).
  useEffect(() => {
    if (context.input?.[name] === undefined) {
      updateInput(name, []);
    }
  }, [name]); // eslint-disable-line

  // Get current items from context
  const items = (context.input?.[name] as string[]) || [];
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  const canAddMore = !maxItems || items.length < maxItems;

  // Stable IDs for sortable items — use index-based keys since items are plain strings
  const itemIds = items.map((_, i) => `bullet-${i}`);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateItems = (updated: string[]) => {
    const filtered = updated.filter((item) => item.trim() !== '');
    updateInput(name, filtered);
  };

  const addItem = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !canAddMore) return;
    updateItems([...items, trimmed]);
    setNewItem('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem(newItem);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes('\n')) {
      e.preventDefault();
      const lines = pastedText
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const available = maxItems ? maxItems - items.length : lines.length;
      const toAdd = lines.slice(0, available);
      updateItems([...items, ...toAdd]);
      setNewItem('');
    }
  };

  const removeItem = (index: number) => {
    updateItems(items.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const updated = [...items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updateItems(updated);
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setEditText(items[index]);
  };

  const confirmEdit = () => {
    if (editingIndex === null) return;
    const updated = [...items];
    const trimmed = editText.trim();
    if (trimmed) {
      updated[editingIndex] = trimmed;
      updateItems(updated);
    } else {
      removeItem(editingIndex);
    }
    setEditingIndex(null);
    setEditText('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = itemIds.indexOf(active.id as string);
    const newIndex = itemIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    updateItems(arrayMove(items, oldIndex, newIndex));
  };

  const inputBaseClasses =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

  const renderItemList = () => {
    if (items.length === 0) return null;

    // When drag is enabled, wrap in DndContext + SortableContext
    if (showDragHandle) {
      return (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <ul className="space-y-1">
              {items.map((item, index) => (
                <SortableBulletItem
                  key={itemIds[index]}
                  id={itemIds[index]}
                  item={item}
                  index={index}
                  itemCount={items.length}
                  showDragHandle={showDragHandle}
                  showArrows={showArrows}
                  isEditing={editingIndex === index}
                  editText={editText}
                  onEditTextChange={setEditText}
                  onConfirmEdit={confirmEdit}
                  onCancelEdit={cancelEdit}
                  onStartEdit={startEdit}
                  onRemove={removeItem}
                  onMove={moveItem}
                  disabled={disabled}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      );
    }

    // Arrows-only: plain list without dnd-kit overhead
    return (
      <ul className="space-y-1">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm"
          >
            <span className="text-muted-foreground select-none">&#8226;</span>
            {editingIndex === index ? (
              <input
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmEdit();
                  }
                  if (e.key === 'Escape') cancelEdit();
                }}
                onBlur={confirmEdit}
                autoFocus
                className="flex-1 bg-transparent border-none outline-none text-foreground"
              />
            ) : (
              <span className="flex-1 text-foreground">{item}</span>
            )}
            {!disabled && editingIndex !== index && (
              <div className="flex items-center gap-1 shrink-0">
                {showArrows && (
                  <>
                    <button
                      type="button"
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      title="Move up"
                    >
                      &#9650;
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(index, 1)}
                      disabled={index === items.length - 1}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                      title="Move down"
                    >
                      &#9660;
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => startEdit(index)}
                  className="p-1 text-muted-foreground hover:text-foreground"
                  title="Edit"
                >
                  &#9998;
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-1 text-muted-foreground hover:text-destructive"
                  title="Delete"
                >
                  &#10005;
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div id={id} className={cn('space-y-2', className)} style={style as CSSProperties}>
      {label && <label className="block text-sm font-medium text-foreground">{label}</label>}

      {/* Item list */}
      {renderItemList()}

      {/* Add new item input */}
      {canAddMore && !disabled && (
        <div className="flex gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(inputBaseClasses, 'flex-1')}
          />
          <button
            type="button"
            onClick={() => addItem(newItem)}
            disabled={!newItem.trim()}
            className="px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      )}

      {maxItems && (
        <p className="text-xs text-muted-foreground">
          {items.length}/{maxItems} items
        </p>
      )}
    </div>
  );
}
